import React, { useState } from "react";
import { Plus } from "lucide-react";
import { fmt, initials } from "./financeUtils";

/**
 * Eğitmen Maaşları (artifact `.tr-card`).
 * trainers: merged balance entries:
 *   { id, name, earned, earnedPrivate, earnedGroup, paid, pending,
 *     sessionCount?, orgSplit: [{orgId, orgName, share}] }
 * canAdd → shows "MAAŞ EKLE"; onAddSalary opens TransactionModal.
 */
const Bar = ({ label, pct, color }) => (
  <div className="flex items-center gap-2 text-[12px]">
    <span className="w-[82px] shrink-0 font-medium text-[var(--fin-text)] truncate">{label}</span>
    <span className="flex-1 h-[7px] rounded bg-[var(--fin-track)] overflow-hidden">
      <span
        className="block h-full rounded transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }}
      />
    </span>
    <span className="fin-grot fin-num w-[42px] text-right font-semibold text-[11.5px] text-[var(--fin-text)]">
      %{Math.round(pct)}
    </span>
  </div>
);

const MiniLabel = ({ children, color }) => (
  <div
    className="text-[10px] font-bold uppercase tracking-[0.08em] mt-3 mb-1.5"
    style={{ color: color || "var(--fin-muted)" }}
  >
    {children}
  </div>
);

const TrainerCard = ({ t }) => {
  const [open, setOpen] = useState(false);
  const kalan = Math.max(0, t.earned - t.paid);
  const badge =
    t.earned - t.paid <= 0.5
      ? { cls: "bg-[var(--fin-pos-soft)] text-[var(--fin-pos)]", label: "Ödendi" }
      : t.paid > 0
        ? { cls: "bg-[var(--fin-warn-soft)] text-[var(--fin-warn)]", label: "Kısmi" }
        : { cls: "bg-[var(--fin-neg-soft)] text-[var(--fin-neg)]", label: "Borç" };

  const dersSplit = [];
  if (t.earned > 0) {
    if (t.earnedPrivate > 0)
      dersSplit.push({ label: "Bireysel", pct: (t.earnedPrivate / t.earned) * 100 });
    if (t.earnedGroup > 0)
      dersSplit.push({ label: "Grup", pct: (t.earnedGroup / t.earned) * 100 });
  }

  const subParts = [];
  if (t.sessionCount > 0) subParts.push(`${t.sessionCount} ders`);
  else if (t.revenue > 0) subParts.push(`${fmt(t.revenue)} ciro`);
  subParts.push(t.orgSplit.map((o) => o.orgName).join(" + "));

  return (
    <div className="border-b border-[var(--fin-line)] last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-[11px] px-4 py-[13px] hover:bg-[var(--fin-hover)] transition-colors text-left"
      >
        <div className="fin-grot w-[38px] h-[38px] rounded-[11px] bg-[var(--fin-avatar-bg)] text-[var(--fin-avatar-fg)] grid place-items-center font-semibold text-[13px] shrink-0">
          {initials(t.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-[var(--fin-text)] truncate">
            {t.name}
          </div>
          <div className="text-[11px] text-[var(--fin-muted)] truncate">
            {subParts.join(" · ")}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="fin-grot fin-num font-semibold text-[14px] text-[var(--fin-text)]">
            {fmt(t.earned)}
          </div>
          <span
            className={`inline-block text-[10px] font-semibold px-[7px] py-0.5 rounded-full mt-0.5 ${badge.cls}`}
          >
            {badge.label}
          </span>
        </div>
        <span
          className={`fin-chev text-[var(--fin-muted)] text-[12px] ${open ? "open" : ""}`}
        >
          ▾
        </span>
      </button>

      <div className={`fin-collapse ${open ? "open" : ""}`}>
        <div className="bg-[var(--fin-hover)]">
          <div className="px-4 pb-4 pt-1.5">
            {dersSplit.length > 0 && (
              <>
                <MiniLabel>Ders tipi kırılımı</MiniLabel>
                <div className="flex flex-col gap-[7px]">
                  {dersSplit.map((d) => (
                    <Bar key={d.label} label={d.label} pct={d.pct} color="var(--fin-pos)" />
                  ))}
                </div>
              </>
            )}

            {t.orgSplit.length > 1 && (
              <>
                <MiniLabel>Şube dağılımı</MiniLabel>
                <div className="flex flex-col gap-[7px]">
                  {t.orgSplit.map((o) => (
                    <Bar
                      key={o.orgId}
                      label={o.orgName}
                      pct={o.share * 100}
                      color="var(--fin-blue)"
                    />
                  ))}
                </div>
              </>
            )}

            <MiniLabel>Ödeme durumu</MiniLabel>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <div className="bg-[var(--fin-card)] border border-[var(--fin-line)] rounded-[10px] px-2.5 py-2">
                <div className="text-[9.5px] font-semibold text-[var(--fin-muted)] uppercase tracking-[0.05em]">
                  Hakediş
                </div>
                <div className="fin-grot fin-num font-semibold text-[13px] mt-px text-[var(--fin-text)]">
                  {fmt(t.earned)}
                </div>
              </div>
              <div className="bg-[var(--fin-card)] border border-[var(--fin-line)] rounded-[10px] px-2.5 py-2">
                <div className="text-[9.5px] font-semibold text-[var(--fin-muted)] uppercase tracking-[0.05em]">
                  Ödenen
                </div>
                <div className="fin-grot fin-num font-semibold text-[13px] mt-px text-[var(--fin-pos)]">
                  {fmt(t.paid)}
                </div>
              </div>
              <div className="bg-[var(--fin-card)] border border-[var(--fin-line)] rounded-[10px] px-2.5 py-2">
                <div className="text-[9.5px] font-semibold text-[var(--fin-muted)] uppercase tracking-[0.05em]">
                  Kalan
                </div>
                <div
                  className="fin-grot fin-num font-semibold text-[13px] mt-px"
                  style={{ color: kalan > 0 ? "var(--fin-neg)" : "var(--fin-pos)" }}
                >
                  {fmt(kalan)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrainerSalaries = ({ trainers, canAdd, onAddSalary }) => (
  <section>
    <div className="flex justify-between items-center gap-2 mx-1 mb-2">
      <h2 className="fin-grot text-[14px] font-semibold text-[var(--fin-text)]">
        Eğitmen Maaşları
      </h2>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-[var(--fin-muted)] hidden sm:inline">
          karta dokun → kırılım
        </span>
        {canAdd && (
          <button
            onClick={onAddSalary}
            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full bg-[var(--fin-blue-soft)] text-[var(--fin-blue)] hover:opacity-80 transition-opacity"
          >
            <Plus className="w-3 h-3" /> Maaş Ekle
          </button>
        )}
      </div>
    </div>
    <div className="fin-card">
      {trainers.length ? (
        trainers.map((t) => <TrainerCard key={t.mergeKey} t={t} />)
      ) : (
        <div className="px-4 py-[26px] text-center text-[12.5px] text-[var(--fin-muted)]">
          Bu dönemde hakediş veya ödeme bulunmuyor.
        </div>
      )}
    </div>
  </section>
);

export default TrainerSalaries;
